import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import actions from './actions';
import { t } from '../../../i18n';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import notifications from '../../../redux/notifications/actions';
import Api from '../../../helpers/api';
import { tableConfig } from '../../../settings';

const invoiceLineItemsApi = new Api('agencies/invoices/line_items');
const holdInvoiceLineItemsApi = new Api('agencies/invoices/line_items/on_hold');
const unHoldInvoiceLineItemsApi = new Api('agencies/invoices/line_items/un_hold');
const consolidatedInvoicesApi = new Api('agencies/invoices/consolidated');
const payConsolidatedInvoicesApi = new Api('agencies/invoices/consolidated/paid');
const payPartiallyConsolidatedInvoicesApi = new Api('agencies/invoices/consolidated/paid_partially');
const createConsolidatedInvoiceFormDataApi = new Api('agencies/invoices/consolidated/create_form_data');

function* showConsolidatedInvoice({ payload }) {
  const { id } = payload;

  // Fix for incorrect URL formatting when ID is passed as a result parameter and :id is at the end of the API URL
  const consolidatedInvoiceApi = new Api(`agencies/invoices/consolidated/${id}`);

  try {
    const result = yield call(sendRequest, consolidatedInvoiceApi.load, {}, notifications.REQUEST_ERROR);

    if (result?.data?.success?.data) {
      const { consolidated_invoice } = result.data.success.data;

      yield put(actions.showConsolidatedInvoiceSuccess(consolidated_invoice));
    } else {
      yield put(actions.showConsolidatedInvoiceError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* createConsolidatedInvoice({ payload }) {
  const { data } = payload;

  try {
    const result = yield call(sendRequest, consolidatedInvoicesApi.createItem, { data }, notifications.REQUEST_ERROR);

    if (result?.status === 200) {
      yield put(actions.createConsolidatedInvoiceSuccess());
    } else {
      yield put(actions.createConsolidatedInvoiceError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadCreateConsolidatedInvoice({ payload }) {
  try {
    const result = yield call(
      sendRequest,
      createConsolidatedInvoiceFormDataApi.createItem,
      { data: { ...payload } },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200 && result?.data) {
      yield put(actions.loadCreateConsolidatedInvoiceSuccess(result.data));
    } else {
      yield put(actions.loadCreateConsolidatedInvoiceError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadInvoiceLineItems({ payload }) {
  const { page = 1, pageSize = tableConfig.pageSize, column, order, filter = '' } = payload || {};

  try {
    const result = yield call(
      sendRequest,
      invoiceLineItemsApi.load,
      { search: { ...payload, page, pageSize, column, order, filter } },
      notifications.REQUEST_ERROR,
    );

    if (result?.data?.success?.data) {
      const { data } = result.data.success;
      const { records, ...rest } = data;
      const recordsWithVirtualId = records.map(item => ({
        ...item,
        virtual_id: `${item.id}-${item.agency.id}`,
      }));

      yield put(
        actions.loadInvoiceLineItemsSuccess({
          records: recordsWithVirtualId,
          ...rest,
        }),
      );
    } else {
      yield put(actions.loadInvoiceLineItemsError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadConsolidatedInvoices({ payload }) {
  const {
    page = 1,
    pageSize = tableConfig.pageSize,
    column,
    order,
    filter = '',
    isAttentionRequired,
    attention_required, // from filter
  } = payload || {};

  try {
    // Check if flagged timesheets feature is enabled
    const state = yield select();
    const flaggedTimesheetsEnabled = state.StartAppSettings?.feature_flags?.flagged_timesheets_enabled;

    if (!flaggedTimesheetsEnabled && (isAttentionRequired || attention_required)) {
      yield put(
        actions.loadConsolidatedInvoicesSuccess({
          records: [],
          isAttentionRequired: true,
        }),
      );

      return;
    }

    const result = yield call(
      sendRequest,
      consolidatedInvoicesApi.load,
      {
        search: {
          ...payload,
          attention_required: isAttentionRequired || attention_required,
          page,
          pageSize,
          column,
          order,
          filter,
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result?.data?.success?.data) {
      const { data } = result.data.success;
      const { records, ...rest } = data;
      const recordsWithVirtualId = records.map(item => ({
        ...item,
        virtual_id: `${item.id}-${item.agency.id}`,
      }));

      yield put(
        actions.loadConsolidatedInvoicesSuccess({
          records: recordsWithVirtualId,
          ...rest,
          isAttentionRequired,
        }),
      );
    } else {
      yield put(actions.loadConsolidatedInvoicesError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* holdInvoiceLineItems({ payload }) {
  const { data, params, cb } = payload;

  try {
    const result = yield call(sendRequest, holdInvoiceLineItemsApi.updateItem, { data }, notifications.REQUEST_ERROR);

    if (result?.status === 200) {
      yield put(actions.holdInvoiceLineItemsSuccess());
      yield put(actions.loadInvoiceLineItemsRequest({ ...params }));

      if (cb) {
        cb();
      }
    } else {
      yield put(actions.holdInvoiceLineItemsError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* unHoldInvoiceLineItems({ payload }) {
  const { data, params, cb } = payload;

  try {
    const result = yield call(sendRequest, unHoldInvoiceLineItemsApi.updateItem, { data }, notifications.REQUEST_ERROR);

    if (result?.status === 200) {
      yield put(actions.unHoldInvoiceLineItemsSuccess());
      yield put(actions.loadInvoiceLineItemsRequest({ ...params }));

      if (cb) {
        cb();
      }
    } else {
      yield put(actions.unHoldInvoiceLineItemsError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* payConsolidatedInvoices({ payload }) {
  const { data, params } = payload;

  try {
    const result = yield call(
      sendRequest,
      payConsolidatedInvoicesApi.updateItem,
      { data },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200) {
      yield put(actions.payConsolidatedInvoicesSuccess());
      yield put(actions.loadConsolidatedInvoicesRequest({ ...params }));
    } else {
      if (result && typeof result === 'object' && !result.status) {
        const validationMessages = Object.values(result).flat().filter(Boolean);

        yield put(
          notifications.requestError({
            error: validationMessages.join(', ') || t('dash.common.notification.default_error_message'),
          }),
        );
      }

      yield put(actions.payConsolidatedInvoicesError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* payPartiallyConsolidatedInvoices({ payload }) {
  const { data, params } = payload;

  try {
    const result = yield call(
      sendRequest,
      payPartiallyConsolidatedInvoicesApi.updateItem,
      { data },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200) {
      yield put(actions.payPartiallyConsolidatedInvoicesSuccess());
      yield put(actions.loadConsolidatedInvoicesRequest({ ...params }));
    } else {
      yield put(actions.payPartiallyConsolidatedInvoicesError());
    }
  } catch (error) {
    logger.error(error);
  }
}

// Ungroup
function* markAsPendingConsolidatedInvoices({ payload }) {
  const { data, params } = payload;

  try {
    const result = yield call(sendRequest, consolidatedInvoicesApi.deleteItem, { data }, notifications.REQUEST_ERROR);

    if (result?.status === 200) {
      yield put(actions.markAsPendingConsolidatedInvoicesSuccess());
      yield put(actions.loadConsolidatedInvoicesRequest({ ...params }));
      yield put(actions.loadConsolidatedInvoicesRequest({ ...params, isAttentionRequired: true }));
      yield put(actions.loadInvoiceLineItemsRequest({ ...params }));
    } else {
      yield put(actions.markAsPendingConsolidatedInvoicesError());
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actions.LOAD_INVOICE_LINE_ITEMS_REQUEST, loadInvoiceLineItems)]);
  yield all([takeEvery(actions.HOLD_INVOICE_LINE_ITEMS_REQUEST, holdInvoiceLineItems)]);
  yield all([takeEvery(actions.UN_HOLD_INVOICE_LINE_ITEMS_REQUEST, unHoldInvoiceLineItems)]);
  yield all([takeEvery(actions.LOAD_CONSOLIDATED_INVOICES_REQUEST, loadConsolidatedInvoices)]);
  yield all([takeEvery(actions.PAY_CONSOLIDATED_INVOICES_REQUEST, payConsolidatedInvoices)]);
  yield all([takeEvery(actions.PAY_PARTIALLY_CONSOLIDATED_INVOICES_REQUEST, payPartiallyConsolidatedInvoices)]);
  yield all([takeEvery(actions.MARK_AS_PENDING_CONSOLIDATED_INVOICES_REQUEST, markAsPendingConsolidatedInvoices)]);
  yield all([takeEvery(actions.LOAD_CREATE_CONSOLIDATED_INVOICE_REQUEST, loadCreateConsolidatedInvoice)]);
  yield all([takeEvery(actions.CREATE_CONSOLIDATED_INVOICE_REQUEST, createConsolidatedInvoice)]);
  yield all([takeEvery(actions.SHOW_CONSOLIDATED_INVOICE_REQUEST, showConsolidatedInvoice)]);
}
